import React, { useState, useEffect, useMemo } from 'react';
import {
  LayoutDashboard,
  Home,
  ShieldCheck,
  FileText,
  ListTodo,
  GitBranch,
  BarChart3,
  TrendingUp,
  Plane,
  GraduationCap,
  FolderOpen,
  Calendar,
  Layers,
  Search,
  Bell,
  Settings,
  HelpCircle,
  FileCheck2,
  AlertTriangle,
  ClipboardCheck,
  Building,
  Key,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Target,
  PanelLeftClose,
  PanelLeftOpen,
  Send,
  LogOut,
  Award,
  Menu,
  X
} from 'lucide-react';

import { DashboardPage } from './modules/dashboard/DashboardPage';
import { IngestionReportingPage } from './modules/ingestion/IngestionReportingPage';
import { AssessReportPage } from './modules/ingestion/AssessReportPage';
import { SurveysPage } from './modules/surveys/SurveysPage';
import { TasksActionsPage } from './modules/tasks_actions/TasksActionsPage';
import { WorkflowsPage } from './modules/workflows/WorkflowsPage';
import { ReportsPage } from './modules/reports/ReportsPage';
import { AnalyticsPage } from './modules/analytics/AnalyticsPage';
import { AssetsPage } from './modules/assets/AssetsPage';
import { TrainingPage } from './modules/training/TrainingPage';
import { DocumentsPage } from './modules/documents/DocumentsPage';
import { MeetingsPage } from './modules/meetings/MeetingsPage';
import { ManagementOfChangePage } from './modules/moc/ManagementOfChangePage';
import { InitiativesPage } from './modules/initiatives/InitiativesPage';
import { ComplianceMonitoringPage } from './modules/compliance/monitoring/ComplianceMonitoringPage';
import { ComplianceMatrixPage } from './modules/compliance/matrix/ComplianceMatrixPage';
import { ObligationsPage } from './modules/compliance/obligations/ObligationsPage';
import { RegulationsPage } from './modules/compliance/regulations/RegulationsPage';
import { RegulatoryFrameworksPage } from './modules/compliance/regulatory/RegulatoryFrameworksPage';
import { CapasPage } from './modules/quality/capa/CapasPage';
import { FindingsPage } from './modules/quality/findings/FindingsPage';
import { InspectionsPage } from './modules/quality/inspections/InspectionsPage';
import { AuditsPage } from './modules/quality/audits/AuditsPage';
import { PerformancePage } from './modules/safety/performance/PerformancePage';
import { ActionsPage } from './modules/safety/actions/ActionsPage';
import { RisksPage } from './modules/safety/risks/RisksPage';
import { HazardsPage } from './modules/safety/hazards/HazardsPage';
import { InvestigationsPage } from './modules/safety/investigations/InvestigationsPage';
import { OccurrencesPage } from './modules/safety/occurrences/OccurrencesPage';
import { RolesPage } from './modules/roles/RolesPage';
import { IntegrationAttemptsPage } from './modules/integrations/IntegrationAttemptsPage';
import { HelpPage } from './modules/help/HelpPage';
import { AdministrationPage } from './modules/administration/AdministrationPage';
import { StandaloneBowtiePage } from './modules/safety/risks/bowtie/StandaloneBowtiePage';
import { NotFoundPage } from './components/NotFoundPage';
import { AccessDeniedPage } from './components/AccessDeniedPage';
import { LoginPage } from './modules/auth/LoginPage';
import { useGlobalModalEscape } from './shared/useGlobalModalEscape';
import { MODULE_META_CATALOG, setPageMeta } from './shared/meta';
import { isModuleAllowedForRole } from './shared/rolePermissions';

// Modular UI components for the new layout
import { CalendarDueModal } from './components/CalendarDueModal';
import { NotificationsMenu } from './components/NotificationsMenu';
import { UserProfileMenu } from './components/UserProfileMenu';
import { SubmitReportModal } from './components/SubmitReportModal';
import { UserProfilePage, ProfileSubTab } from './modules/profile/UserProfilePage';

interface AuthSessionUser {
  id: string;
  username: string;
  display_name: string;
  user_type: string;
  primary_email: string;
  organization: {
    id: string;
    code: string;
    name: string;
  };
}

const DEFAULT_AUTH_USER: AuthSessionUser = {
  id: 'usr_erdal_aydeniz',
  username: 'erdal_aydeniz',
  display_name: 'Erdal Aydeniz',
  user_type: 'SAFETY_MANAGER',
  primary_email: 'luminixaviation@gmail.com',
  organization: {
    id: 'a0000000-0000-0000-0000-000000000001',
    code: 'ORG-XYZ',
    name: 'Luminix Aviation',
  },
};

const normalizeAuthUser = (raw: any): AuthSessionUser => {
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_AUTH_USER;
  }
  const rawOrg = raw.organization;
  const orgObj =
    rawOrg && typeof rawOrg === 'object'
      ? {
          id: String(rawOrg.id || 'org_luminix'),
          code: String(rawOrg.code === 'XYZ_AIR' || !rawOrg.code ? 'ORG-XYZ' : rawOrg.code),
          name: String(rawOrg.name || 'Luminix Aviation'),
        }
      : {
          id: typeof rawOrg === 'string' ? rawOrg : 'org_luminix',
          code: 'ORG-XYZ',
          name: 'Luminix Aviation',
        };

  return {
    id: String(raw.id || 'usr_erdal_aydeniz'),
    username: String(raw.username || 'erdal_aydeniz'),
    display_name: String(raw.display_name || raw.username || 'Erdal Aydeniz'),
    user_type: String(raw.user_type || raw.role || 'SAFETY_MANAGER'),
    primary_email: String(raw.primary_email || raw.email || 'luminixaviation@gmail.com'),
    organization: orgObj,
  };
};

interface SidebarItem {
  id: string;
  title: string;
  icon: React.ReactNode;
}

interface SidebarCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  iconColor?: string;
  items: SidebarItem[];
}

const getInitialTab = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const tabParam = urlParams.get('tab');
  if (tabParam === 'admin' || tabParam === 'modules') return tabParam;
  if (tabParam) return tabParam;

  const path = window.location.pathname.replace(/^\/|\/$/g, '');
  if (path && path !== 'index.html') {
    return path;
  }

  // Default view is the Dashboard matching the UI mockup
  return 'dashboard';
};

export const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>(getInitialTab);
  const [selectedBowtieRiskId, setSelectedBowtieRiskId] = useState<string | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('riskId');
  });

  const [profileSubTab, setProfileSubTab] = useState<ProfileSubTab>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sub = urlParams.get('subTab');
    if (sub === 'account_info' || sub === 'change_password') return sub;
    return 'system_settings';
  });

  const [currentUser, setCurrentUser] = useState<AuthSessionUser | null>(() => {
    try {
      const saved = localStorage.getItem('auth_user');
      const token = localStorage.getItem('auth_token');
      if (saved && token) {
        const parsed = JSON.parse(saved);
        if (parsed) return normalizeAuthUser(parsed);
      }
      return null;
    } catch {
      return null;
    }
  });

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('sidebar_collapsed');
      if (saved !== null) return saved === 'true';
      return true; // Default to collapsed/mini-sidebar per user preference
    } catch {
      return true;
    }
  });

  const [isSidebarHovered, setIsSidebarHovered] = useState<boolean>(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const isExpanded = isMobileMenuOpen || !isSidebarCollapsed || isSidebarHovered;

  // Requirement: "Default olarak akordiyon menüler kapalı gelmeli"
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({
    sms: false,
    qms: false,
    compliance: false,
    governance: false,
    operations: false,
    intelligence: false,
    administration: false,
  });

  // Header popups state
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

  // Global escape key handler closes top-most open modal/pop-up
  useGlobalModalEscape(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      return;
    }
    if (isSubmitModalOpen) setIsSubmitModalOpen(false);
    if (isCalendarOpen) setIsCalendarOpen(false);
    if (isNotificationsOpen) setIsNotificationsOpen(false);
  });

  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('sidebar_collapsed', String(next));
      } catch {}
      return next;
    });
  };

  // Complete regulatory and platform navigation catalog matching system architecture
  const sidebarCategories: SidebarCategory[] = [
    {
      id: 'sms',
      title: 'Safety Management System',
      icon: <AlertTriangle size={17} color="#dc2626" />,
      items: [
        { id: 'ingestion', title: 'Safety Reports', icon: <FileText size={17} /> },
        { id: 'occurrences', title: 'Occurrences', icon: <AlertTriangle size={17} /> },
        { id: 'investigations', title: 'Safety Investigations', icon: <Search size={17} /> },
        { id: 'hazards', title: 'Hazard Register', icon: <AlertTriangle size={17} /> },
        { id: 'risks', title: 'Risk Assessment & Bowtie', icon: <ShieldCheck size={17} /> },
        { id: 'actions', title: 'Mitigation & Verification', icon: <Layers size={17} /> },
        { id: 'performance', title: 'Safety Performance (SPI/SPT)', icon: <TrendingUp size={17} /> },
      ],
    },
    {
      id: 'qms',
      title: 'Quality Management',
      icon: <Award size={17} color="#2563eb" />,
      items: [
        { id: 'audits', title: 'Audit Planning & Schedule', icon: <Calendar size={17} /> },
        { id: 'inspections', title: 'Audits & Inspections', icon: <ClipboardCheck size={17} /> },
        { id: 'findings', title: 'Findings & Non-Conformities', icon: <AlertTriangle size={17} /> },
        { id: 'capas', title: 'Corrective Actions (CAPA)', icon: <ListTodo size={17} /> },
        { id: 'vendor_audits', title: 'Supplier & Vendor Audits', icon: <Building size={17} /> },
      ],
    },
    {
      id: 'compliance',
      title: 'Compliance Management',
      icon: <ShieldCheck size={17} color="#0d9488" />,
      items: [
        { id: 'monitoring', title: 'Compliance Monitoring', icon: <ShieldCheck size={17} /> },
        { id: 'regulatory', title: 'Regulatory Requirements', icon: <Building size={17} /> },
        { id: 'regulations', title: 'Regulations & Standards', icon: <FolderOpen size={17} /> },
        { id: 'obligations', title: 'Exemptions & Deviations', icon: <FileText size={17} /> },
        { id: 'matrix', title: 'Authority Oversight', icon: <Layers size={17} /> },
      ],
    },
    {
      id: 'governance',
      title: 'Governance & Change',
      icon: <Target size={17} color="#7c3aed" />,
      items: [
        { id: 'meetings', title: 'Meetings', icon: <Calendar size={17} /> },
        { id: 'moc', title: 'Management of Change (MoC)', icon: <Layers size={17} /> },
        { id: 'initiatives', title: 'Strategic Initiatives', icon: <Target size={17} /> },
        { id: 'surveys', title: 'Safety Culture & Surveys', icon: <FileCheck2 size={17} /> },
        { id: 'tasks_actions', title: 'Central Tasks & Actions', icon: <ListTodo size={17} /> },
      ],
    },
    {
      id: 'operations',
      title: 'Documents & Training',
      icon: <FolderOpen size={17} color="#059669" />,
      items: [
        { id: 'documents', title: 'Controlled Documents', icon: <FolderOpen size={17} /> },
        { id: 'training', title: 'Training & Qualifications', icon: <GraduationCap size={17} /> },
        { id: 'assets', title: 'Aviation Fleet Assets', icon: <Plane size={17} /> },
      ],
    },
    {
      id: 'intelligence',
      title: 'Intelligence & Analytics',
      icon: <BarChart3 size={17} color="#d97706" />,
      items: [
        { id: 'reports', title: 'Reports & Regulatory Returns', icon: <BarChart3 size={17} /> },
        { id: 'analytics', title: 'Analytics & Visual Dashboards', icon: <TrendingUp size={17} /> },
        { id: 'workflows', title: 'Automated Workflows', icon: <GitBranch size={17} /> },
      ],
    },
    {
      id: 'administration',
      title: 'Administration',
      icon: <Settings size={17} color="#475569" />,
      items: [
        { id: 'administration', title: 'Organization & System Setup', icon: <Settings size={17} /> },
        { id: 'roles', title: 'Users, Roles & Permissions', icon: <Key size={17} /> },
        { id: 'integration_attempts', title: 'Integration Hub & Telemetry', icon: <ShieldCheck size={17} /> },
        { id: 'help', title: 'Help & Support Center', icon: <HelpCircle size={17} /> },
      ],
    },
  ];

  // Filter sidebar categories to ONLY show categories and items the user is authorized to access
  const allowedSidebarCategories: SidebarCategory[] = useMemo(() => {
    const role = currentUser?.user_type || '';
    return sidebarCategories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((item) => isModuleAllowedForRole(role, item.id)),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [currentUser?.user_type]);

  const toggleCategory = (categoryId: string) => {
    // If sidebar is collapsed and not hovered, expand it
    if (isSidebarCollapsed && !isSidebarHovered) {
      setIsSidebarCollapsed(false);
      setOpenCategories((prev) => ({
        ...prev,
        [categoryId]: true,
      }));
      return;
    }

    setOpenCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Auto-expand the category containing the active tab
  useEffect(() => {
    const found = allowedSidebarCategories.find((cat) =>
      cat.items.some((item) => item.id === activeTab)
    );
    if (found && !openCategories[found.id]) {
      setOpenCategories((prev) => ({
        ...prev,
        [found.id]: true,
      }));
    }
  }, [activeTab, allowedSidebarCategories]);

  // Central tab navigation: updates activeTab and syncs browser URL
  const navigateTab = (tabId: string, extraParams?: Record<string, string | null>) => {
    setIsMobileMenuOpen(false);
    // Normalize aliases
    const target = tabId === 'vendor_audits' ? 'audits' : tabId === 'admin' ? 'modules' : tabId;

    if (currentUser && !isModuleAllowedForRole(currentUser.user_type, target)) {
      setActiveTab(target);
    } else {
      setActiveTab(target);
    }

    const params = new URLSearchParams();
    params.set('tab', target);
    if (extraParams) {
      Object.entries(extraParams).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });
      if (extraParams.subTab) {
        setProfileSubTab(extraParams.subTab as ProfileSubTab);
      }
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ tab: target }, '', newUrl);
  };

  // Dynamically update document title, description, keywords, Open Graph & Twitter meta tags
  useEffect(() => {
    const meta = MODULE_META_CATALOG[activeTab] || MODULE_META_CATALOG['not_found'];
    setPageMeta(meta);
  }, [activeTab]);

  // Sync initial URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('tab')) {
      params.set('tab', 'dashboard');
      window.history.replaceState({ tab: 'dashboard' }, '', `${window.location.pathname}?${params.toString()}`);
    }
  }, []);

  // Listen to browser Back / Forward buttons and sync activeTab
  useEffect(() => {
    const handlePopState = () => {
      setIsMobileMenuOpen(false);
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab') || 'dashboard';
      const normalized = tab === 'admin' ? 'modules' : tab;
      setActiveTab(normalized);
      const riskId = params.get('riskId');
      setSelectedBowtieRiskId(riskId);
      const sub = params.get('subTab');
      if (sub === 'account_info' || sub === 'change_password' || sub === 'system_settings') {
        setProfileSubTab(sub as ProfileSubTab);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleNavigateToBowtie = (riskId: string) => {
    setSelectedBowtieRiskId(riskId);
    navigateTab('bowtie', { riskId });
  };

  const handleLoginSuccess = (_token: string, user: AuthSessionUser) => {
    setCurrentUser(user);
    const tab = getInitialTab();
    navigateTab(tab);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    try {
      sessionStorage.clear();
    } catch {}
    setCurrentUser(null);
    setActiveTab('dashboard');
    // Clear URL query parameters and pathname cleanly back to '/'
    window.history.replaceState({}, '', '/');
  };

  // Format today's date (e.g. "Tue, 28 Oct 2026")
  const getFormattedToday = () => {
    const d = new Date();
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  if (!currentUser) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // If active tab is standalone bowtie, render full-screen Bowtie Workspace
  if (activeTab === 'bowtie') {
    return (
      <StandaloneBowtiePage
        initialRiskId={selectedBowtieRiskId || undefined}
        onReturnToPlatform={() => {
          navigateTab('risks');
        }}
      />
    );
  }

  return (
    <div className={`app-container ${isSidebarCollapsed ? 'sidebar-is-collapsed' : ''}`}>
      {/* Mobile Drawer Backdrop */}
      <div
        className={`mobile-sidebar-backdrop ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* 1. Left Sidebar with Logo, Slogan, and Hover-to-Expand Navigation */}
      <div className={`sidebar-wrapper ${isSidebarCollapsed ? 'collapsed' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        <aside
          className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${isSidebarCollapsed && isSidebarHovered ? 'hover-expanded' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}
          onMouseEnter={() => setIsSidebarHovered(true)}
          onMouseLeave={() => setIsSidebarHovered(false)}
        >
          {/* Sleek Floating Edge Toggle Button (Desktop only) */}
          <button
            type="button"
            className="sidebar-edge-toggle"
            onClick={toggleSidebarCollapse}
            title={isSidebarCollapsed ? "Sidebar'ı Sabitle (Açık Tut)" : "Sidebar'ı Otomatik Kapat (İmleç ile Açılır)"}
            aria-label="Toggle sidebar collapse"
          >
            {isSidebarCollapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
          </button>

          {/* Brand Header: Logo + Slogan */}
          <div className="sidebar-brand-header">
            <div className="sidebar-brand-main" onClick={() => { navigateTab('dashboard'); setIsMobileMenuOpen(false); }}>
              <img
                src="/logo.png"
                alt="Luminix Logo"
                className="sidebar-logo-img"
              />
              {isExpanded && (
                <div className="sidebar-brand-text">
                  <span className="sidebar-brand-title">LUMINIX</span>
                  <span className="sidebar-brand-slogan">SAFETY QUALITY COMPLIANCE</span>
                </div>
              )}
            </div>
            {isMobileMenuOpen && (
              <button
                type="button"
                className="mobile-sidebar-close-btn"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close navigation drawer"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Sidebar Nav Items */}
          <nav className="sidebar-nav">
            {/* Dashboard item (Always prominent on top) */}
            <button
              type="button"
              onClick={() => navigateTab('dashboard')}
              className={`nav-item nav-item-dashboard ${activeTab === 'dashboard' ? 'active' : ''}`}
              title="Dashboard"
            >
              <Home size={18} />
              {isExpanded && <span>Dashboard</span>}
            </button>

            {/* Accordion Categories */}
            {allowedSidebarCategories.map((cat) => {
              const isOpen = Boolean(openCategories[cat.id]);
              const hasActiveChild = cat.items.some((m) => m.id === activeTab);

              return (
                <div key={cat.id} className="sidebar-accordion-group">
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat.id)}
                    className={`sidebar-accordion-header ${hasActiveChild ? 'has-active' : ''} ${isOpen ? 'is-open' : ''}`}
                    title={cat.title}
                  >
                    <span className="sidebar-accordion-title-wrap">
                      {cat.icon}
                      {isExpanded && (
                        <span className="sidebar-accordion-title-text">{cat.title}</span>
                      )}
                    </span>
                    {isExpanded && (
                      <span className="sidebar-accordion-chevron">
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </span>
                    )}
                  </button>

                  {isOpen && isExpanded && (
                    <div className="sidebar-accordion-items">
                      {cat.items.map((mod) => (
                        <button
                          key={mod.id}
                          type="button"
                          onClick={() => { navigateTab(mod.id); setIsMobileMenuOpen(false); }}
                          className={`nav-item ${activeTab === mod.id ? 'active' : ''}`}
                          title={mod.title}
                        >
                          {mod.icon}
                          <span className="nav-item-text">{mod.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            </nav>
        </aside>
      </div>

      {/* 2. Main Content Wrapper */}
      <div className="main-content">
        {/* Top Header */}
        <header className="top-header">
          <div className="header-left">
            {/* Mobile Hamburger Drawer Toggle */}
            <button
              type="button"
              className="mobile-menu-toggle-btn"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-label="Toggle navigation drawer"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Mobile Brand Logo & Title */}
            <div className="mobile-header-brand" onClick={() => navigateTab('dashboard')}>
              <img src="/logo.png" alt="Luminix" className="mobile-header-logo" />
              <span className="mobile-header-title">LUMINIX</span>
            </div>

            {/* Search Input with shortcut tag */}
            <div className="search-bar">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search reports, occurrences, investigations..."
              />
              <span className="search-kbd-shortcut">⌘K</span>
            </div>

            {/* Dark Submit Report Pill Button */}
            <button
              type="button"
              className="btn-submit-report"
              onClick={() => setIsSubmitModalOpen(true)}
              title="Submit Safety & Quality Report"
            >
              <Send size={15} />
              <span className="btn-submit-text">Submit Report</span>
            </button>
          </div>

          <div className="header-right">
            {/* Note: User explicitly requested: "Sadee Notes kısmı olmasın" -> Notes button is omitted! */}

            {/* Date Widget with clickable Pop-up Calendar */}
            <button
              type="button"
              className="header-date-pill-btn"
              onClick={() => setIsCalendarOpen(true)}
              title="Click to view work schedule & due dates calendar"
            >
              <Calendar size={15} />
              <span className="header-date-text">{getFormattedToday()}</span>
            </button>

            {/* Notification Bell with Badge */}
            <button
              type="button"
              className="header-notif-btn"
              onClick={() => setIsNotificationsOpen(true)}
              title="Notifications & Work Updates"
              aria-label="Notifications"
            >
              <Bell size={18} />
              <span className="header-notif-badge">3</span>
            </button>

            {/* Profile Section on Top Right */}
            <UserProfileMenu
              userName={currentUser?.display_name || 'Erdal Aydeniz'}
              userRole={currentUser?.user_type === 'SAFETY_MANAGER' ? 'Safety Manager' : currentUser?.user_type || 'User'}
              userEmail={currentUser?.primary_email || 'luminixaviation@gmail.com'}
              department="Flight Safety & Quality Directorate"
              position="Safety Manager"
              staffId="EMP-2026-084"
              station="LTFM / Istanbul Airport"
              organizationName={currentUser?.organization?.name || 'Luminix Aviation'}
              organizationCode={currentUser?.organization?.code || 'XYZ_AIR'}
              onLogout={handleLogout}
              onNavigate={(tab, extraParams) => navigateTab(tab, extraParams)}
            />

            {/* Header Quick Sign Out Button as seen in reference mockup */}
            <button
              type="button"
              className="header-logout-btn"
              onClick={handleLogout}
              title="Sign Out"
              aria-label="Sign Out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>

        {/* Calendar Due Dates Modal */}
        <CalendarDueModal
          isOpen={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
          onNavigate={(tab) => navigateTab(tab)}
        />

        {/* Notifications Popover Menu */}
        <NotificationsMenu
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
          onNavigate={(tab) => navigateTab(tab)}
        />

        {/* Submit Report Modal */}
        <SubmitReportModal
          isOpen={isSubmitModalOpen}
          onClose={() => setIsSubmitModalOpen(false)}
          onNavigateToIngestion={() => navigateTab('ingestion')}
        />

        {/* Page Container */}
        <main className="page-container">
          {activeTab !== 'modules' && activeTab !== 'admin' && !isModuleAllowedForRole(currentUser?.user_type || '', activeTab) ? (
            <AccessDeniedPage
              moduleName={activeTab}
              userRole={currentUser?.user_type || 'User'}
              onNavigate={(tab) => navigateTab(tab)}
            />
          ) : activeTab === 'dashboard' ? (
            <DashboardPage
              userName={(currentUser?.display_name || 'Erdal').split(' ')[0]}
              userRole={currentUser?.user_type || 'SAFETY_MANAGER'}
              userId={currentUser?.id || 'usr_erdal_aydeniz'}
              onNavigate={(tab, extraParams) => navigateTab(tab, extraParams)}
            />
          ) : activeTab === 'modules' || activeTab === 'admin' ? (
            <div>
              <div className="page-header-banner">
                <div className="badge">LUMINIX ENTERPRISE AVIATION PLATFORM</div>
                <h1>All Platform Modules</h1>
                <p>Safety Management (SMS), Quality Assurance (QMS), Regulatory Compliance, Governance & Operations.</p>
              </div>

              {/* Grouped Module Cards by Regulatory Category */}
              {allowedSidebarCategories.map((cat) => (
                <div key={cat.id} className="module-category-section">
                  <div className="module-category-header">
                    <div className="module-category-title">
                      {cat.icon}
                      <span>{cat.title}</span>
                    </div>
                  </div>
                  <div className="module-grid">
                    {cat.items.map((mod) => (
                      <div
                        key={mod.id}
                        className="module-card"
                        onClick={() => navigateTab(mod.id)}
                      >
                        <div className="module-icon">
                          {mod.icon}
                        </div>
                        <div className="module-content">
                          <h3>{mod.title}</h3>
                          <p>Open and manage {mod.title} module</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : activeTab === 'assess_report' ? (
            <AssessReportPage onBack={() => navigateTab('ingestion', { subTab: 'waiting_assessment' })} />
          ) : activeTab === 'ingestion' ? (
            <IngestionReportingPage onNavigateTab={(tab, extra) => navigateTab(tab, extra)} />
          ) : activeTab === 'surveys' ? (
            <SurveysPage />
          ) : activeTab === 'tasks_actions' ? (
            <TasksActionsPage />
          ) : activeTab === 'workflows' ? (
            <WorkflowsPage />
          ) : activeTab === 'reports' ? (
            <ReportsPage />
          ): (activeTab === 'analytics' || activeTab === 'analysis') ? (
            <AnalyticsPage initialTab={activeTab === 'analysis' ? 'dashboards' : undefined} />
          ) : activeTab === 'assets' ? (
            <AssetsPage />
          ) : activeTab === 'training' ? (
            <TrainingPage />
          ) : activeTab === 'documents' ? (
            <DocumentsPage />
          ) : activeTab === 'meetings' ? (
            <MeetingsPage />
          ) : activeTab === 'initiatives' ? (
            <InitiativesPage />
          ) : activeTab === 'moc' ? (
            <ManagementOfChangePage />
          ) : activeTab === 'monitoring' ? (
            <ComplianceMonitoringPage />
          ) : activeTab === 'matrix' ? (
            <ComplianceMatrixPage />
          ) : activeTab === 'obligations' ? (
            <ObligationsPage />
          ) : activeTab === 'regulations' ? (
            <RegulationsPage />
          ) : activeTab === 'regulatory' ? (
            <RegulatoryFrameworksPage />
          ) : activeTab === 'capas' ? (
            <CapasPage />
          ) : activeTab === 'findings' ? (
            <FindingsPage />
          ) : activeTab === 'inspections' ? (
            <InspectionsPage />
          ) : activeTab === 'audits' ? (
            <AuditsPage />
          ) : activeTab === 'performance' ? (
            <PerformancePage />
          ) : activeTab === 'actions' ? (
            <ActionsPage />
          ) : activeTab === 'risks' ? (
            <RisksPage
              initialRiskId={selectedBowtieRiskId || undefined}
              initialView={selectedBowtieRiskId ? 'bowtie' : 'register'}
            />
          ) : activeTab === 'hazards' ? (
            <HazardsPage onNavigateToBowtie={handleNavigateToBowtie} />
          ) : activeTab === 'investigations' ? (
            <InvestigationsPage />
          ) : activeTab === 'occurrences' ? (
            <OccurrencesPage />
          ) : activeTab === 'roles' ? (
            <RolesPage />
          ) : activeTab === 'user_profile' || activeTab === 'profile' ? (
            <UserProfilePage
              initialSubTab={profileSubTab}
              userName={currentUser?.display_name || 'Erdal Aydeniz'}
              username={currentUser?.username || 'erdal.aydeniz'}
              userRole={currentUser?.user_type || 'SAFETY_MANAGER'}
              userEmail={currentUser?.primary_email || 'luminixaviation@gmail.com'}
              department="Flight Safety & Quality Directorate"
              position="Safety Manager"
              staffId="EMP-2026-084"
              station="LTFM / Istanbul Airport"
              organizationName={currentUser?.organization?.name || 'Luminix Aviation'}
              organizationCode={currentUser?.organization?.code || 'XYZ_AIR'}
              onNavigate={(tab, extraParams) => navigateTab(tab, extraParams)}
            />
          ) : activeTab === 'administration' ? (
            <AdministrationPage />
          ) : activeTab === 'integration_attempts' ? (
            <IntegrationAttemptsPage />
          ) : activeTab === 'help' ? (
            <HelpPage />
          ) : (
            <NotFoundPage
              invalidTab={activeTab}
              onNavigate={(tab) => navigateTab(tab)}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default App;
